import React from "react"
import autobind from "class-autobind"
import TimelineTableLeftSide from "./TimelineTableLeftSide"
import TimelineTableRightSide from "./TimelineTableRightSide"
import { IScheduleTimelineLeftSideColumn, IScheduleDailyTimelineData, ISimpleTime, IJobDocument, IClientDocument } from "../../../models"
import { compareObjects } from "./TimelineTableLeftSide"
import * as scheduleHelper from "../../../helpers/scheduleHelper"
import DragDropRow from "./DragDropRow"

interface IProps {
  items: any[]
  timelineData: IScheduleDailyTimelineData[][]
  leftSideColumns: IScheduleTimelineLeftSideColumn[]
  timelineTableWidth: number
  leftSideWidth?: number
  showCheckboxes?: boolean
  selectable?: boolean
  selectIdentityPropertyNames?: string[]
  multiSelectable?: boolean
  readonly: boolean
  allowDragDrop?: boolean
  dontNeedLeftSideTable?: boolean
  referenceFieldNameForEdit?: string
  referenceFieldNameToIdentifyPatternTemplate?: string
  handleUpdate?(row: any): void
  handleSelect?(rows: any[]): void
  timelineItemRenderElement(timelineItem: IScheduleDailyTimelineData, rowNumber: number, orderNumber: number): JSX.Element
  handleUpdateTimelineData?(timelineItem: IScheduleDailyTimelineData, rowNumber: number, orderNumber: number): void
  checkValidateNewTimeline?(timelineItem: IScheduleDailyTimelineData, rowNumber: number, orderNumber: number): boolean
  handleDeleteTimelineItem?(rowNumber: number, orderNumber: number): void
  handleUpdateTimelineItem?(item: any, rowNumber: number, orderNumber: number)
  handleDrag?(dragDropRowId: string, moveUp: number, reorderItems: any[]): void
  handleDrop?(): void

  // allow to edit by Tab
  allowQuickEditTime?: boolean
  excludeNonProductive?: boolean

  scheduleJob?: IJobDocument
  scheduleClient?: IClientDocument
}
interface IState {
  selectedRows: any[]
  showTable: boolean
  leftSideWidth: number
  rightSideWidth: number
  widthPerHour: number
  coordinateTime: ISimpleTime
  timelineBarHourLength: number
  hoveredRowNumber: number
  dragDropRowId: string
}

export default class TimelineTable extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)
    this.state = {
      selectedRows: [],
      showTable: true,
      leftSideWidth: this.props.leftSideWidth ? this.props.leftSideWidth : 200,
      rightSideWidth: 0,
      widthPerHour: 0,
      timelineBarHourLength: 24,
      coordinateTime: scheduleHelper.getCoordinateTime(props.timelineData),
      hoveredRowNumber: null,
      dragDropRowId: null,
    }
    autobind(this)
  }

  componentDidMount() {
    this.setWidths(this.props)
    this.setCoordinateTime(this.props)
  }

  componentWillReceiveProps(nextProps) {
    this.setWidths(nextProps)
    this.setCoordinateTime(nextProps)
  }

  setWidths(props) {
    const timelineBarHourLength = scheduleHelper.getTimelineBarHourLength(props.timelineData)
    const widthInfo = scheduleHelper.getScheduleTimelineRightSideWidthInfo(timelineBarHourLength, props.timelineTableWidth, this.state.leftSideWidth)
    this.setState({ widthPerHour: widthInfo.widthPerHour, rightSideWidth: widthInfo.rightSideWidth, timelineBarHourLength })
  }

  setCoordinateTime(props: IProps) {
    const coordinateTime = scheduleHelper.getCoordinateTime(props.timelineData)
    this.setState({ coordinateTime })
  }

  handelOnCellClick(rowNumber, colId) {
    const clickedRow = this.props.items[rowNumber]
    if (colId === -1) {
      if (!this.props.selectable) return
      let selectedRows = this.state.selectedRows.slice()
      if (this.state.selectedRows.find((item) => compareObjects(this.props.selectIdentityPropertyNames, item, clickedRow))) {
        selectedRows = selectedRows.filter((item) => !compareObjects(this.props.selectIdentityPropertyNames, item, clickedRow))
      }
      else {
        selectedRows.push(clickedRow)
      }
      this.setSelectedRows(selectedRows)
    }
    else {
      this.props.handleUpdate && this.props.handleUpdate(clickedRow)
    }
  }

  handleOnRowSelection(result) {
    result === "all" && this.setSelectedRows(this.props.items.slice())
    result === "none" && this.setSelectedRows([])
  }

  setSelectedRows(selectedRows) {
    this.setState({ selectedRows, showTable: false }, () => this.setState({ showTable: true }))
    this.props.handleSelect && this.props.handleSelect(selectedRows)
  }

  render() {
    const items = this.props.excludeNonProductive ? this.props.items.filter(item => !scheduleHelper.isNonProductive(item)) : this.props.items

    return (
      <div id="ScheduleMoveItemCoordinate" style={{ display: "flex", position: "relative" }}>
        {!this.props.dontNeedLeftSideTable &&
          <div style={{ width: this.state.leftSideWidth }}>
            <TimelineTableLeftSide
              items={items}
              selectedItems={this.state.selectedRows}
              columns={this.props.leftSideColumns}
              selectable={this.props.selectable}
              selectIdentityPropertyNames={this.props.selectIdentityPropertyNames}
              multiSelectable={this.props.multiSelectable}
              showCheckboxes={this.props.showCheckboxes !== undefined ? this.props.showCheckboxes : true}
              handelOnCellClick={this.handelOnCellClick}
              handleOnRowSelection={this.handleOnRowSelection}
              hoveredRowNumber={this.state.hoveredRowNumber}
              handleOnRowHover={(hoveredRowNumber) => this.setState({ hoveredRowNumber })}
              handleOnRowHoverExit={() => this.setState({ hoveredRowNumber: null })}
              readonly={this.props.readonly}
              allowDragDrop={this.props.allowDragDrop}
              dragDropRowId={this.state.dragDropRowId}
              handleSetDragDropRowId={dragDropRowId => this.setState({ dragDropRowId })}
            />
          </div>
        }
        <div style={{ width: this.state.rightSideWidth }}>
          <TimelineTableRightSide
            items={items}
            timelineItemRenderElement={this.props.timelineItemRenderElement}
            timelineData={this.props.timelineData}
            selectedItems={this.state.selectedRows}
            widthPerHour={this.state.widthPerHour}
            coordinateTime={this.state.coordinateTime}
            timelineBarHourLength={this.state.timelineBarHourLength}
            width={this.state.rightSideWidth}
            showRowHover={!this.props.dontNeedLeftSideTable}
            handleUpdate={this.props.handleUpdate}
            hoveredRowNumber={this.state.hoveredRowNumber}
            handleOnRowHover={(hoveredRowNumber) => this.setState({ hoveredRowNumber })}
            handleOnRowHoverExit={() => this.setState({ hoveredRowNumber: null })}
            handleUpdateTimelineData={this.props.handleUpdateTimelineData}
            checkValidateNewTimeline={this.props.checkValidateNewTimeline}
            handleDeleteTimelineItem={this.props.handleDeleteTimelineItem}
            handleUpdateTimelineItem={this.props.handleUpdateTimelineItem}
            readonly={this.props.readonly}
            referenceFieldNameForEdit={this.props.referenceFieldNameForEdit}
            referenceFieldNameToIdentifyPatternTemplate={this.props.referenceFieldNameToIdentifyPatternTemplate}
            dragDropRowId={this.state.dragDropRowId}
            allowQuickEditTime={this.props.allowQuickEditTime}
            scheduleJob={this.props.scheduleJob}
            scheduleClient={this.props.scheduleClient}
          />
        </div>
        {this.state.dragDropRowId &&
          <DragDropRow
            items={items}
            dragDropRowId={this.state.dragDropRowId}
            leftSideWidth={this.props.leftSideWidth}
            rightSideWidth={this.state.rightSideWidth}
            handleDrag={moveUp => this.props.handleDrag(this.state.dragDropRowId, moveUp, this.props.items)}
            handleDrop={() => {
              this.props.handleDrop()
              this.setState({ dragDropRowId: null })
            }}
            handleCancleDragDrop={() => this.setState({dragDropRowId: null})}
          />
        }
      </div>
    )
  }
}